import { useMemo, useState } from "react";
import { Link } from "wouter";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowRight,
  Info,
  Send,
  ShieldCheck,
  ShieldAlert,
  PlugZap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useDashboard } from "@/features/dashboard/dashboard-context";
import {
  OP_LABEL,
  countryOf,
  fmt,
  fmtDate,
  signed,
  type Currency,
  type Tx,
} from "@/types/dashboard";
import {
  EnvironmentBadge,
  Flag,
  KybLock,
  PageHead,
  Panel,
  StatusPill,
} from "@/components/bits";
import {
  PaymentDialog,
  type PaymentKind,
} from "@/features/dashboard/payment-dialog";
import { TxDetailDialog } from "@/components/tx-detail-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { NotificationDetail } from "@/features/notifications/notifications-page";
import { toast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/i18n-context";

const RANGES = [
  { k: "7", l: "7j" },
  { k: "30", l: "30j" },
  { k: "90", l: "90j" },
  { k: "all", l: "Tout" },
] as const;

export function inRange(tx: Tx, range: string) {
  if (range === "all") return true;
  return (
    Date.now() - new Date(tx.createdAt).getTime() <= Number(range) * 86400_000
  );
}

function Metric({
  label,
  hint,
  children,
}: {
  label: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-background/60 p-4 transition hover:border-primary/30">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        {label}
        <Tooltip>
          <TooltipTrigger aria-label={hint}>
            <Info className="h-3.5 w-3.5" />
          </TooltipTrigger>
          <TooltipContent className="max-w-56">{hint}</TooltipContent>
        </Tooltip>
      </p>
      <div className="mt-2">{children}</div>
    </div>
  );
}

export default function Overview() {
  const d = useDashboard();
  const { t, lang } = useI18n();
  const [range, setRange] = useState("30");
  const [sim, setSim] = useState<PaymentKind | null>(null);
  const [detail, setDetail] = useState<Tx | null>(null);
  const [notificationId, setNotificationId] = useState<string | null>(null);

  const txs = useMemo(
    () => d.transactions.filter((t) => inRange(t, range)),
    [d.transactions, range],
  );
  const completed = txs.filter((t) => t.status === "COMPLETED");
  const volume = useMemo(() => {
    const m = new Map<Currency, number>();
    completed.forEach((t) =>
      m.set(t.currency, (m.get(t.currency) ?? 0) + t.amount),
    );
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [completed]);
  const markets = new Set(completed.map((t) => t.country)).size;
  const unread = d.notices.filter((n) => !n.read);
  const complianceCase = (d.compliance?.case ?? d.compliance ?? {}) as Record<
    string,
    unknown
  >;
  const verificationStatus = String(
    complianceCase.status ??
      d.account.verification_status ??
      (d.isBusiness ? d.account.kyb_status : d.account.kyc_status) ??
      "NON COMMUNIQUÉ",
  );

  return (
    <>
      <PageHead
        title={t("overview.title")}
        sub={t("overview.subWelcome", {
          name: d.profile.name,
          space: d.isBusiness
            ? t("overview.businessSpace")
            : t("overview.individualSpace"),
        })}
      >
        <EnvironmentBadge />
        {d.financialAllowed && (
          <>
            <Button
              data-testid="button-deposit"
              onClick={() => setSim("DEPOSIT")}
              className="gap-1.5"
            >
              <ArrowDownToLine className="h-4 w-4" />
              {t("overview.deposit")}
            </Button>
            <Button
              data-testid="button-withdraw"
              variant="outline"
              onClick={() => setSim("WITHDRAWAL")}
              className="gap-1.5"
            >
              <ArrowUpFromLine className="h-4 w-4" />
              {t("overview.withdraw")}
            </Button>
            <Button
              data-testid="button-transfer"
              variant="outline"
              onClick={() => setSim("TRANSFER")}
              className="gap-1.5"
            >
              <Send className="h-4 w-4" />
              {t("overview.transfer")}
            </Button>
          </>
        )}
      </PageHead>
      {d.loading && (
        <p role="status" className="mb-4 text-sm">
          {t("overview.loadingOverview")}
        </p>
      )}
      {d.error && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-destructive p-4 text-sm"
        >
          {d.error}{" "}
          <Button variant="outline" onClick={() => void d.refresh()}>
            {t("common.retry")}
          </Button>
        </div>
      )}
      {Object.keys(d.sectionErrors).length > 0 && (
        <div
          role="status"
          className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950"
        >
          Certaines sections sont indisponibles :{" "}
          {Object.entries(d.sectionErrors)
            .map(([name, message]) => `${name} — ${message}`)
            .join(" · ")}
          . Les montants manquants ne représentent pas un solde nul.
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-5">
          <Panel
            title={t("overview.paymentOverview")}
            sub={t("overview.periodSub")}
            action={
              <div
                role="group"
                aria-label="Période"
                className="flex rounded-lg bg-muted p-0.5"
              >
                {RANGES.map((r) => (
                  <button
                    key={r.k}
                    data-testid={`range-${r.k}`}
                    aria-pressed={range === r.k}
                    onClick={() => setRange(r.k)}
                    className={cn(
                      "rounded-md px-2.5 py-1 text-xs font-semibold transition",
                      range === r.k
                        ? "bg-card text-primary shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {r.k === "all"
                      ? lang === "en"
                        ? "All"
                        : "Tout"
                      : `${r.k}${lang === "en" ? "d" : "j"}`}
                  </button>
                ))}
              </div>
            }
          >
            {d.locked ? (
              <KybLock what={t("overview.paymentOverview")} />
            ) : (
              <div className="grid gap-3 md:grid-cols-[1fr_1.4fr_1fr]">
                <Metric
                  label={t("overview.transactions")}
                  hint={t("overview.transactionsHint")}
                >
                  <p
                    data-testid="metric-count"
                    className="tnum text-3xl font-extrabold"
                  >
                    {d.sectionErrors.transactions ? "—" : completed.length}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {lang === "en"
                      ? `of ${txs.length} total`
                      : `sur ${txs.length} au total`}
                  </p>
                </Metric>
                <Metric
                  label={t("overview.totalVolume")}
                  hint={t("overview.totalVolumeHint")}
                >
                  {volume.length === 0 ? (
                    <p className="text-3xl font-extrabold">—</p>
                  ) : (
                    <ul className="space-y-0.5">
                      {volume.map(([c, v]) => (
                        <li
                          key={c}
                          className="tnum flex justify-between text-sm"
                        >
                          <span className="font-mono text-xs text-muted-foreground">
                            {c}
                          </span>
                          <span className="font-bold">{fmt(v, c)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Metric>
                <Metric
                  label={t("overview.activeMarkets")}
                  hint={t("overview.activeMarketsHint")}
                >
                  <p
                    data-testid="metric-markets"
                    className="tnum text-3xl font-extrabold"
                  >
                    {markets}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {lang === "en"
                      ? `across ${d.wallets.length} wallets`
                      : `sur ${d.wallets.length} wallets`}
                  </p>
                </Metric>
              </div>
            )}
          </Panel>

          <Panel
            title={t("overview.balancesAndWallets")}
            sub={
              lang === "en"
                ? `Spread across ${d.wallets.length} countries · each balance in its own currency`
                : `Répartis sur ${d.wallets.length} pays · chaque solde dans sa propre devise`
            }
            action={
              <Link
                href="/wallets"
                data-testid="link-all-wallets"
                className="rounded-md border px-3 py-1.5 text-xs font-semibold transition hover:border-primary/40 hover:text-primary"
              >
                {t("overview.allWallets")}
              </Link>
            }
          >
            {d.locked ? (
              <KybLock what="Les wallets" />
            ) : (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {d.wallets.map((w) => (
                  <Link
                    key={w.id}
                    href={`/wallets?w=${w.id}`}
                    data-testid={`card-wallet-${w.country}`}
                    className="group flex min-h-[92px] flex-col justify-between rounded-lg border p-3 transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-[13px] font-semibold leading-tight">
                        {countryOf(w.country).name}
                      </span>
                      <Flag code={w.country} />
                    </div>
                    <div>
                      <p className="tnum text-[15px] font-extrabold">
                        {fmt(w.balance, w.currency).replace(
                          ` ${w.currency}`,
                          "",
                        )}{" "}
                        <span className="text-xs font-semibold text-muted-foreground">
                          {w.currency}
                        </span>
                      </p>
                      {w.status === "PENDING" && (
                        <p className="text-[10px] font-semibold text-amber-700">
                          Accès pays en attente
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Panel>

          <Panel
            title={t("overview.recentTransactions")}
            action={
              <Link
                href="/transactions"
                className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
              >
                {t("nav.history")} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            }
          >
            {d.locked ? (
              <KybLock what={t("nav.history")} />
            ) : txs.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t("overview.emptyTxs")}
              </p>
            ) : (
              <ul className="-mx-2 divide-y">
                {txs.slice(0, 6).map((tTx) => (
                  <li key={tTx.reference}>
                    <button
                      data-testid={`row-recent-${tTx.reference}`}
                      onClick={() => setDetail(tTx)}
                      className="flex w-full items-center gap-3 rounded-md px-2 py-2.5 text-left transition hover:bg-accent/50"
                    >
                      <Flag code={tTx.country} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-semibold">
                          {t(`operations.${tTx.operation}`) ||
                            OP_LABEL[tTx.operation]}{" "}
                          · {tTx.counterparty}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {fmtDate(tTx.createdAt)} ·{" "}
                          <span className="font-mono">{tTx.reference}</span>
                        </p>
                      </div>
                      <StatusPill status={tTx.status} />
                      <span
                        className={cn(
                          "tnum w-32 text-right text-sm font-bold",
                          signed(tTx) > 0 && "text-emerald-700",
                        )}
                      >
                        {signed(tTx) < 0 ? "−" : "+"}
                        {fmt(tTx.amount, tTx.currency)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        <div className="space-y-5">
          <Panel
            title={t("nav.verification")}
            action={
              <Link
                href="/verification"
                className="rounded-md border px-3 py-1.5 text-xs font-semibold transition hover:border-primary/40 hover:text-primary"
              >
                {t("common.details")}
              </Link>
            }
          >
            {verificationStatus !== "APPROVED" ? (
              <div className="flex gap-2.5 rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
                <ShieldAlert className="h-4 w-4 shrink-0" />
                <span>
                  <b>
                    {d.isBusiness ? "KYB" : "KYC"} {verificationStatus}
                  </b>
                  {d.locked
                    ? lang === "en"
                      ? " — financial and business modules remain locked until validation."
                      : " — les modules financiers et entreprise sont verrouillés jusqu’à validation."
                    : lang === "en"
                      ? " — review your file for required documents."
                      : " — consultez le dossier pour connaître les exigences."}
                </span>
              </div>
            ) : (
              <div className="flex gap-2.5 rounded-lg bg-emerald-50 p-3 text-xs text-emerald-900">
                <ShieldCheck className="h-4 w-4 shrink-0" />
                <span>
                  <b>{d.isBusiness ? "KYB" : "KYC"} APPROVED</b>
                  {lang === "en"
                    ? " — verification approved by compliance."
                    : " — vérification approuvée par le service de conformité."}
                </span>
              </div>
            )}
          </Panel>

          <Panel
            title={`${t("nav.notifications")} (${unread.length})`}
            sub={
              lang === "en"
                ? "Recent account notices"
                : "Alertes récentes du compte"
            }
            action={
              <Link
                href="/notifications"
                data-testid="button-overview-mark-read"
                className="rounded-md border px-3 py-1.5 text-xs font-semibold transition hover:border-primary/40 hover:text-primary"
              >
                {t("common.viewAll")}
              </Link>
            }
          >
            <ul className="-mx-1 divide-y">
              {d.notices.slice(0, 4).map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => {
                      setNotificationId(n.id);
                      void d.markRead(n.id).catch((error: unknown) =>
                        toast({
                          title: "Notifications",
                          description:
                            error instanceof Error
                              ? error.message
                              : "Lecture impossible",
                          variant: "destructive",
                        }),
                      );
                    }}
                    className="w-full rounded-md px-1 py-3 text-left transition hover:bg-accent/40"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                        {n.type === "PAYMENT"
                          ? "Paiement"
                          : n.type === "COMPLIANCE"
                            ? "Conformité"
                            : "Système"}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {fmtDate(n.createdAt)}
                      </span>
                    </div>
                    <p className="mt-1.5 flex items-center justify-between gap-1.5 text-[13px] font-semibold">
                      <span className="flex items-center gap-1.5 truncate">
                        {!n.read && (
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        )}
                        <span className="truncate">{n.title}</span>
                      </span>
                      {n.status && (
                        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                          {n.status}
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                      {n.body}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </Panel>

          <Panel title="État des services">
            <div className="flex gap-2.5 text-xs leading-relaxed text-muted-foreground">
              <PlugZap className="h-4 w-4 shrink-0 text-primary" />
              <span>
                Le suivi d'état des opérateurs est présenté depuis les services
                configurés pour votre compte.
              </span>
            </div>
          </Panel>
        </div>
      </div>

      <PaymentDialog kind={sim} onClose={() => setSim(null)} />
      <TxDetailDialog tx={detail} onClose={() => setDetail(null)} />
      <Dialog
        open={Boolean(notificationId)}
        onOpenChange={(open) => !open && setNotificationId(null)}
      >
        <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-[520px]">
          {notificationId && <NotificationDetail id={notificationId} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
